import { addDoc, deleteDoc, doc, getDoc, onSnapshot, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { getConverter } from '../../../firebase';
import { createAppAsyncThunk } from '../../../hooks';
import type { AppThunk } from '../../../store';
import { getNextAlphaString } from '../../../util/getNextAlphaString';
import getStytchMemberId from '../../../util/getStytchMemberId';
import getStytchOrg from '../../../util/getStytchOrgId';
import { CompData, type SubTakeData } from '../../RecordingSessionDataGrid/types';
import { sortBySubTake } from '../../RecordingSessionDataGrid/util';
import { EpisodeData, type SelectInfoItem, TakeGroup } from '../types';
import {
    episodeCompsRef,
    episodeDataRef, episodeSelectInfoRef, episodeSelectInfosRef,
    episodeTakeGroupsRef, getNextTakeNumberString,
} from '../util';
import { setComps, setEpisodeData, setTakeGroups, setSubTakeIdToFocus, setSelectInfos } from './actions';
import { selectTakeGroups } from './selectors';

export const subscribeToEpisodeData =
  (sessionId: string, episodeId: string): AppThunk<() => void> =>
      (dispatch) => {
          const org = getStytchOrg();

          const epConverter = getConverter<EpisodeData>(org ?? null);
          const compsConverter = getConverter<CompData>(org ?? null);
          const groupsConverter = getConverter<TakeGroup>(org ?? null);
          const selectInfoConverter = getConverter<SelectInfoItem>(org ?? null);

          const episodeRef = episodeDataRef(sessionId, episodeId).withConverter(
              epConverter
          );
          const compsRef = episodeCompsRef(sessionId, episodeId).withConverter(
              compsConverter
          );
          const groupsRef = episodeTakeGroupsRef(sessionId, episodeId).withConverter(
              groupsConverter
          );
          const selectInfosRef = episodeSelectInfosRef(sessionId, episodeId).withConverter(
              selectInfoConverter
          );

          const epDataUnsub = onSnapshot(episodeRef, (snap) => {
              const data = snap.data();
              if (!data) return;
              dispatch(
                  setEpisodeData({
                      episodeId,
                      updatedAt: data.updatedAt,
                  })
              );
          });

          const compsUnsub = onSnapshot(
              query(compsRef, where('orgId', '==', org)),
              (snap) => {
                  const data = snap.docs.map((d) => d.data());
                  dispatch(setComps(data));
              }
          );

          const groupsUnsub = onSnapshot(
              query(groupsRef, where('orgId', '==', org)),
              (snap) => {
                  const data = snap.docs.map((d) => d.data());
                  dispatch(setTakeGroups(data));
              }
          );

          const selectInfosUnsub = onSnapshot(
              query(selectInfosRef, where('orgId', '==', org)),
              (snap) => {
                  const data = snap.docs.map((d) => d.data());
                  dispatch(setSelectInfos(data));
              }
          );

          const unsubFuncs = [epDataUnsub, compsUnsub, groupsUnsub, selectInfosUnsub];
          return () => {
              for (const func of unsubFuncs) func();
          };
      };

type addTakeParams = {
    sessionId: string
    episodeId: string
    takeGroupId: string
}

export const addTake = createAppAsyncThunk(
    'recordingSesssion/takes/add',
    async ({ sessionId, episodeId, takeGroupId }: addTakeParams, thunkAPI) => {
        const org = getStytchOrg();
        const memberId = getStytchMemberId();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');
        if (!memberId) return thunkAPI.rejectWithValue('Member not found');

        const groupConverter = getConverter<TakeGroup>(org);
        const takeGroupRef = doc(episodeTakeGroupsRef(sessionId, episodeId), takeGroupId).withConverter(groupConverter);

        const state = thunkAPI.getState();
        const takeGroups = selectTakeGroups(state);
        const group = takeGroups.find((g) => g.id === takeGroupId);

        if (!group) return thunkAPI.rejectWithValue('Group not found');

        const takes = group.subTakes.toSorted(sortBySubTake);
        const nextSubTake = getNextAlphaString(takes.lastItem?.subTake ?? '');

        const newTake = {
            id: uuidv4(),
            groupId: takeGroupId,
            note: '',
            subTake: nextSubTake,
        };

        const newSubTakes = [...takes, newTake];

        thunkAPI.dispatch(setSubTakeIdToFocus(newTake.id));

        await updateDoc(takeGroupRef, {
            subTakes: newSubTakes,
        })
    }
);

type updateTakeParams = {
    sessionId: string
    episodeId: string
    takeGroupId: string
    takeId: string
    takeData: Partial<Omit<SubTakeData, 'id' | 'circle'>>
}

export const updateTake = createAppAsyncThunk(
    'recordingSesssion/takes/update',
    async ({ sessionId, episodeId, takeGroupId, takeId, takeData }: updateTakeParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const groupConverter = getConverter<TakeGroup>(org);
        const takeGroupRef = doc(episodeTakeGroupsRef(sessionId, episodeId), takeGroupId).withConverter(groupConverter);

        const state = thunkAPI.getState();
        const groups = selectTakeGroups(state);

        const group = groups.find((g) => g.id === takeGroupId);

        if (!group) return thunkAPI.rejectWithValue('Group not found');

        const newSubTakes = group.subTakes.map((t) => {
            if (t.id === takeId) {
                return {
                    ...t,
                    ...takeData,
                };
            }
            return t;
        });

        await updateDoc(takeGroupRef, {
            subTakes: newSubTakes,
        });
    }
);

type deleteTakeParams = {
    sessionId: string
    episodeId: string
    takeGroupId: string
    takeId: string
}

export const deleteTake = createAppAsyncThunk(
    'recordingSesssion/takes/delete',
    async ({ sessionId, episodeId, takeGroupId, takeId }: deleteTakeParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const groupConverter = getConverter<TakeGroup>(org);
        const takeGroupRef = doc(episodeTakeGroupsRef(sessionId, episodeId), takeGroupId).withConverter(groupConverter);

        const state = thunkAPI.getState();
        const groups = selectTakeGroups(state);

        const group = groups.find((g) => g.id === takeGroupId);

        if (!group) return thunkAPI.rejectWithValue('Group not found');

        const newSubTakes = group.subTakes.filter((t) => t.id !== takeId);

        // TODO: check if take is in use in comps

        if (newSubTakes.length === 0) {
            await deleteDoc(takeGroupRef);
        } else {
            await updateDoc(takeGroupRef, {
                subTakes: newSubTakes,
            });
        }
    }
);

type addTakeGroupParams = {
    sessionId: string
    episodeId: string
    lineIds: string[]
}

export const addTakeGroup = createAppAsyncThunk(
    'recordingSesssion/takeGroups/add',
    async ({ sessionId, episodeId, lineIds }: addTakeGroupParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const groupConverter = getConverter<TakeGroup>(org);
        const takeGroupRef = episodeTakeGroupsRef(sessionId, episodeId).withConverter(groupConverter);

        const state = thunkAPI.getState();
        const takeGroups = selectTakeGroups(state);
        const nextTake = getNextTakeNumberString(takeGroups);

        const result = await addDoc(takeGroupRef, {
            take: nextTake,
            subTakes: [],
            lineIds,
            ordinal: takeGroups.length,
        })

        thunkAPI.dispatch(addTake({
            sessionId,
            episodeId,
            takeGroupId: result.id,
        }));

        return result.id;
    }
);

type updateTakeGroupParams = {
    sessionId: string
    episodeId: string
    takeGroupId: string
    takeGroupData: Partial<Omit<TakeGroup, 'id' | 'subTakes'>>
}

export const updateTakeGroup = createAppAsyncThunk(
    'recordingSesssion/takeGroups/update',
    async ({ sessionId, episodeId, takeGroupId, takeGroupData }: updateTakeGroupParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const groupConverter = getConverter<TakeGroup>(org);
        const takeGroupRef = doc(episodeTakeGroupsRef(sessionId, episodeId), takeGroupId).withConverter(groupConverter);

        const state = thunkAPI.getState();
        const group = selectTakeGroups(state).find((g) => g.id === takeGroupId);

        if (!group) return thunkAPI.rejectWithValue('Group not found');

        await updateDoc(takeGroupRef, takeGroupData);
    }
);

type deleteTakeGroupParams = {
    sessionId: string
    episodeId: string
    takeGroupId: string
}

export const deleteTakeGroup = createAppAsyncThunk(
    'recordingSesssion/takeGroups/delete',
    async ({ sessionId, episodeId, takeGroupId }: deleteTakeGroupParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const groupConverter = getConverter<TakeGroup>(org);
        const takeGroupRef = doc(episodeTakeGroupsRef(sessionId, episodeId), takeGroupId).withConverter(groupConverter);
        await deleteDoc(takeGroupRef);
    }
);

type CircleTakeForLineParams = {
    sessionId: string
    episodeId: string
    revisionChainId: string
    takeId: string
}

export const toggleCircleTake = createAppAsyncThunk(
    'recordingSession/take/circle',
    async ({ sessionId, episodeId, revisionChainId, takeId }: CircleTakeForLineParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const selectConverter = getConverter<SelectInfoItem>(org);
        const selectInfoRef = episodeSelectInfoRef(sessionId, episodeId, revisionChainId).withConverter(selectConverter);

        const doc = await getDoc(selectInfoRef);
        const selectInfo = doc.exists() ? doc.data() : undefined;
        const update = {
            circle: selectInfo?.circle ?? '',
            alt: selectInfo?.alt ?? [],
        } satisfies SelectInfoItem;

        if (selectInfo && selectInfo.circle === takeId) {
            update.circle = '';
        } else {
            update.circle = takeId;
        }

        if (selectInfo && selectInfo.alt && selectInfo.alt.includes(takeId)) {
            update.alt = update.alt?.filter((id) => id !== takeId) ?? [];
        }
        if (selectInfo && selectInfo.circle && selectInfo.circle !== '' && selectInfo.circle !== takeId) {
            update.alt = [...(update?.alt ?? []), selectInfo.circle];
        }

        if (doc.exists()) {
            await updateDoc(selectInfoRef, update);
        } else {
            await setDoc(selectInfoRef, update);
        }
    }
);

type AltTakeForLineParams = {
    sessionId: string
    episodeId: string
    revisionChainId: string
    takeId: string
}

export const toggleAltTake = createAppAsyncThunk(
    'recordingSession/take/alt',
    async ({ sessionId, episodeId, revisionChainId, takeId }: AltTakeForLineParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const selectConverter = getConverter<SelectInfoItem>(org);
        const selectInfoRef = episodeSelectInfoRef(sessionId, episodeId, revisionChainId).withConverter(selectConverter);

        const doc = await getDoc(selectInfoRef);
        const selectInfo = doc.exists() ? doc.data() : undefined;
        const update = {} as Partial<SelectInfoItem>;

        if (selectInfo && selectInfo.alt && selectInfo.alt.includes(takeId)) {
            update.alt = selectInfo.alt.filter((id) => id !== takeId);
        } else {
            update.alt = [...(selectInfo?.alt ?? []), takeId];
        }

        if (selectInfo && selectInfo.circle === takeId) {
            update.circle = '';
        }

        if (doc.exists()) {
            await updateDoc(selectInfoRef, update);
        } else {
            await setDoc(selectInfoRef, update);
        }
    }
);